import { StoreType, SubType, UpdateTxData } from "models/business.model";
import { LoginInfo, SignUpData, UpdateUserData } from "models/user.model";
import { http, rawHttp } from "utils/http";

export interface BaseParamData {
  startDate?: string;
  endDate?: string;
  invoiceType?: SubType;
}

export interface UploadNewzenData {
  bizCode: string;
  ids: string[]; // 선택된 데이터 ID 목록
  isAll?: boolean; // 전체 데이터 업로드 여부 (true: 이미 전송된 데이터 삭제 후 재전송, false: 이미 전송된 데이터 제외한 나머지만 전송)
  deleteOnly?: boolean; // 데이터 삭제만 원하는 경우 true
  invoiceType?: SubType;
}

export function sendLoginRequest(data: LoginInfo): Promise<any> {
  return rawHttp.post("/login", data);
}

export function sendLogoutRequest(): Promise<any> {
  return http.post("/logout");
}

// 로그인 유저 정보
export function getUserInfo(): Promise<any> {
  return http.get("/user");
}

// 유저 정보 수정
export function updateUserInfo(
  userId: string,
  data: UpdateUserData
): Promise<any> {
  return http.put(`/user/${userId}`, data);
}

// 사업자 목록
export function getBusinessList(): Promise<any> {
  return http.get("/business");
}

const smartCtaUrl = "/smartcta";

const storeTypeUrlBuilder = (type: StoreType): string => {
  switch (type) {
    case "card":
      return "/card-buy";
    case "bank":
      return "/bank-statement";
    case "tax":
      return "/invoice";
    default:
      throw new Error(
        "Invalid store type provided. Use 'card', 'bank', or 'tax'."
      );
  }
};

// 사업자 회계처리 현황
export function getStores(
  type: StoreType,
  paramData?: BaseParamData
): Promise<any> {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  return http.get(reqUrl, { params: paramData });
}

// 회계처리 상세 내역
export function getStore(
  type: StoreType,
  businessId: string,
  paramData: BaseParamData
): Promise<any> {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (businessId) reqUrl += `/${businessId}`;
  else throw new Error("Business ID is required.");

  return http.get(reqUrl, { params: paramData });
}

export function updateWorker(businessId: string, incharge: string) {
  return http.put("/smartcta/incharge", { businessId, incharge });
}

// 회계처리 내용 변경
export function updateTransaction(
  type: StoreType,
  businessId: string,
  data: UpdateTxData[],
  paramData?: { registerAccountCode?: boolean }
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/${businessId}`;

  return http.put(reqUrl, data, { params: paramData });
}

// 회계 검수 완료/미완료 처리
export function updateConfirmation(
  type: StoreType,
  businessId: string,
  confirmed: boolean,
  paramData: BaseParamData
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/${businessId}/confirmation`;

  return http.put(reqUrl, {
    isConfirmed: confirmed,
    ...paramData,
  });
}

// 세무사랑 양식 엑셀 다운로드 (통장은 세무사랑 양식이 없음)
export type InvoiceSumCheckInvoiceType = "BUY" | "SALES";

export interface InvoiceSumCheckErrorStore {
  bizNo: string;
  bizName: string;
  dbCount: number;
  hometaxCount: number;
  dbSum: number | string;
  hometaxSum: number | string;
}

export interface InvoiceSumCheckResult {
  isError: boolean;
  dbSum: number;
  hometaxSum: number;
  errorStoreInfos: InvoiceSumCheckErrorStore[];
}

export function getInvoiceSumCheck(
  businessId: string,
  params: { invoiceType: InvoiceSumCheckInvoiceType; date: string }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.get(`${smartCtaUrl}/invoice/sum-check/${businessId}`, {
    params,
  });
}

export function downloadNewzenExcel(
  storeType: StoreType,
  businessId: string,
  paramData: {
    startDate: string;
    endDate: string;
    invoiceType?: string;
  }
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(storeType);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/excel/${businessId}`;

  return rawHttp.get(reqUrl, {
    params: paramData,
    responseType: "blob",
  });
}

// 임시 비밀번호 발급 (이메일)
export function sendTempPassword(mailData: { email: string }) {
  return http.post(`/find-passwd`, mailData);
}

// 유저 인증 코드 발송
export function sendAuthCodeOnDevice(
  device: "email" | "mobile",
  serial: string
) {
  const dvcSerial = device === "email" ? encodeURIComponent(serial) : serial;
  return http.post(`/code/${device}?deviceSerial=${dvcSerial}`);
}

// 유저 인증 코드 검증
export function checkDeviceVeriCode(
  device: "email" | "mobile",
  verId: string,
  code: string
) {
  return http.get(`/code/${device}/${verId}?code=${code}`);
}

// 회원 가입 파일 첨부
export function signUpAttachFile(formData: FormData) {
  return http.post("/attached-file", formData);
}

// 유저 등록 (가입)
export function registerUser(data: SignUpData) {
  return http.post("/register", data);
}

//// 스마트세무사

/// 사업용 신용카드
// 세무사랑 회사 조회 - 카드/은행 공통
export function getNewzenBusinessList(bizId: string): Promise<any> {
  return http.get(`/smartcta/newzen/${bizId}`);
}

// 세무사랑 업로드
export function uploadNewzenStore(
  bizId: string,
  data: UploadNewzenData,
  type?: StoreType
): Promise<any> {
  let reqUrl = smartCtaUrl;
  if (!type) throw new Error("Store type is required.");
  reqUrl += storeTypeUrlBuilder(type);
  reqUrl += "/newzen";

  if (!bizId) throw new Error("Business ID is required.");
  reqUrl += `/${bizId}`;

  return http.post(reqUrl, data);
}

// 세무사랑 데이터 동기화 (다운로드)
export function syncNewzenData(
  bizId: string,
  data: UploadNewzenData,
  storeType?: StoreType
): Promise<any> {
  if (!bizId) throw new Error("Business ID is required.");

  let reqUrl = smartCtaUrl;
  if (!storeType) throw new Error("Store type is required.");
  reqUrl += storeTypeUrlBuilder(storeType);
  reqUrl += "/newzen";

  if (!bizId) throw new Error("Business ID is required.");
  reqUrl += `/${bizId}`;

  return http.put(reqUrl, data);
}

interface MatchHistorySetting {
  modifyPastData: boolean; // 과거 데이터 일괄 수정 여부
  registerKeyword: boolean; // 키워드 등록하여 추후 데이터 일괄 적용 여부
}

export interface MatchStoreStatementRequest extends MatchHistorySetting {
  storeId: string | null; // 거래처 ID (null인 경우 거래처 해제)
}

// 은행입출금내역 매칭 거래처 변경
/**
 * 은행입출금내역 매칭 거래처 변경
 *
 * @param params bankStatementId 은행입출금내역 ID
 * @param body storeId 거래처 ID
 * @param body modifyPastData 과거 데이터 수정 여부
 * @param body registerKeyword 키워드 등록 여부 - 추후 키워드 매칭 데이터 수정 여부
 */
export function putBankStatementMatchingStore(
  bankStatementId: string,
  reqData: MatchStoreStatementRequest
) {
  return http.put(`/resource/bank-statement/${bankStatementId}/store`, reqData);
}

export function putRecordsMatchingStore(
  storeType: StoreType,
  businessId: string,
  storeId: string,
  recordIds: string[]
) {
  let reqUrl = "/store";
  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += "/" + businessId;
  if (!storeId) throw new Error("Store ID is required.");
  reqUrl += "/" + storeId;
  reqUrl += storeTypeUrlBuilder(storeType);

  let reqBodyKey = "";
  switch (storeType) {
    case "bank":
      reqBodyKey = "bankStatementIds";
      break;
    case "card":
      reqBodyKey = "cardBuyIds";
      break;
    default:
      throw new Error("Invalid store type");
  }

  return http.put(reqUrl, {
    [reqBodyKey]: recordIds,
  });
}
